import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Truck, Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v || 0);

function Row({ c, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ name: c.name, price: c.price });
  const save = async (payload) => {
    try { await axios.put(`${API}/shop/shipping-countries/${c.id}`, { name: form.name, price: parseFloat(form.price) || 0, active: c.active, ...payload }); toast.success('País actualizado'); setEdit(false); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
  };
  const remove = async () => {
    if (!window.confirm(`¿Eliminar ${c.name}? Los clientes de ese país no podrán reservar.`)) return;
    try { await axios.delete(`${API}/shop/shipping-countries/${c.id}`); toast.success('País eliminado'); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };
  return (
    <div className="flex items-center gap-2 py-2 border-b border-slate-100 last:border-0" data-testid={`shipping-row-${c.id}`}>
      {edit ? (
        <>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-8 flex-1" data-testid={`shipping-edit-name-${c.id}`} />
          <Input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="h-8 w-28" data-testid={`shipping-edit-price-${c.id}`} />
          <Button size="icon" variant="ghost" className="h-8 w-8 text-green-600" onClick={() => save()} data-testid={`shipping-save-${c.id}`}><Check size={15} /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEdit(false)}><X size={15} /></Button>
        </>
      ) : (
        <>
          <span className={`flex-1 text-sm ${c.active ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{c.name}</span>
          <span className="w-28 text-right text-sm font-semibold text-orange-600">{c.price > 0 ? eur(c.price) : 'Gratis'}</span>
          <Switch checked={c.active} onCheckedChange={(v) => save({ active: v })} data-testid={`shipping-active-${c.id}`} />
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEdit(true)} data-testid={`shipping-edit-${c.id}`}><Pencil size={14} /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={remove} data-testid={`shipping-delete-${c.id}`}><Trash2 size={14} /></Button>
        </>
      )}
    </div>
  );
}

export default function ShippingCountries({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: '', price: '' });
  const load = () => axios.get(`${API}/shop/shipping-countries`).then((r) => setItems(r.data)).catch(() => toast.error('Error al cargar países'));
  useEffect(() => { if (open) load(); }, [open]);

  const add = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/shop/shipping-countries`, { name: form.name, price: parseFloat(form.price) || 0, active: true }); toast.success('País añadido'); setForm({ name: '', price: '' }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error al añadir'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="shipping-dialog">
        <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl flex items-center gap-2"><Truck size={18} className="text-orange-500" /> Países y precios de envío</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">El cliente elige su país al reservar y el envío se suma al precio de la bicicleta. Si no hay países configurados, el envío es 0 €.</p>
        <form onSubmit={add} className="flex gap-2">
          <Input placeholder="País" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-9 flex-1" data-testid="shipping-new-name" />
          <Input type="number" step="0.01" min="0" placeholder="€ envío" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="h-9 w-28" data-testid="shipping-new-price" />
          <Button type="submit" size="sm" className="h-9 bg-orange-500 hover:bg-orange-600 gap-1" data-testid="shipping-add-btn"><Plus size={14} /> Añadir</Button>
        </form>
        <div className="max-h-80 overflow-y-auto" data-testid="shipping-list">
          {items.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">Sin países configurados</p> : items.map((c) => <Row key={c.id} c={c} onSaved={load} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
